import { forwardRef, useId } from "react";

import { FormHelperText } from "@mui/material";

import { useTimeRangeField } from "./TimeRangeField.hooks";
import {
  AddonWrapper,
  Container,
  InputWrapper,
  StylesTimeRangeField,
} from "./TimeRangeField.styles";
import {
  TimeRangeFieldProps,
  TimeRangeFieldReference,
} from "./TimeRangeField.types";
import InputLabel from "../InputLabel/InputLabel";

const TimeRangeField = forwardRef<TimeRangeFieldReference, TimeRangeFieldProps>(
  (
    {
      label,
      id,
      defaultValue,
      value,
      readOnly,
      fullWidth = true,
      InputProps = {},
      addonBefore,
      addonBeforeProps,
      addonAfter,
      addonAfterProps,
      helperText,
      tooltipText,
      onChange,
      ...innerProps
    },
    ref
  ) => {
    const { timeRangeFieldRef, valueState, onChangeHandler } =
      useTimeRangeField({
        ref,
        defaultValue,
        value,
        onChange,
      });
    const formId = useId();

    return (
      <Container>
        {!!label && (
          <InputLabel
            htmlFor={id ?? formId}
            required={innerProps.required}
            helperText={tooltipText}
          >
            {label}
          </InputLabel>
        )}
        <InputWrapper fullWidth={fullWidth} color={innerProps.color}>
          {addonBefore ? (
            <AddonWrapper
              placement="before"
              disabled={innerProps.disabled}
              {...addonBeforeProps}
            >
              {addonBefore}
            </AddonWrapper>
          ) : null}
          <StylesTimeRangeField
            withAddonBefore={!!addonBefore}
            withAddonAfter={!!addonAfter}
            id={id ?? formId}
            variant="outlined"
            size="small"
            ref={timeRangeFieldRef}
            fullWidth={fullWidth}
            value={valueState}
            onChange={onChangeHandler}
            InputProps={{
              readOnly,
              ...InputProps,
            }}
            {...innerProps}
          />
          {addonAfter ? (
            <AddonWrapper
              placement="after"
              disabled={innerProps.disabled}
              {...addonAfterProps}
            >
              {addonAfter}
            </AddonWrapper>
          ) : null}
        </InputWrapper>
        {helperText ? (
          <FormHelperText
            variant="outlined"
            required={innerProps.required}
            error={innerProps.error}
            disabled={innerProps.disabled}
          >
            {helperText}
          </FormHelperText>
        ) : null}
      </Container>
    );
  }
);

export default TimeRangeField;
